import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';

import './Article.scss';
import Popup from '../Popup/Popup';
import { convert } from '../../../helpers/date';
import { useMobile } from '../../../helpers/hooks';
import { getString } from '../../../helpers/styles';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import Transition from '../../Transition/Transition';
import { srcSet, useDimensions } from '../../../helpers/image';
import parseContentHTML from '../../../helpers/parseContentHTML';
import {
  getAdvertisementData,
  getContentReference
} from '../../../helpers/metadata';
import LoginPopupContent from '../PopupContent/LoginPopupContent';

const CommentList = dynamic(() => import('../CommentList/CommentList.jsx'));
const ExpertBanner = dynamic(() => import('../../ExpertBanner/ExpertBanner'));
const CalculationTool = dynamic(() =>
  import('../../CalculationTool/CalculationTool')
);
const SurveyQuickForm = dynamic(() =>
  import('../SurveyQuickForm/SurveyQuickForm.jsx')
);
const ArticleAdvertisement = dynamic(() =>
  import('../../ArticleAdvertisement/ArticleAdvertisement')
);
const ContentCalendarCarousel = dynamic(() =>
  import('../ContentCalendarCarousel/ContentCalendarCarousel')
);

import Dot from '../../../shared/assets/icons/redesign/dot.svg';
import Share from '../../../shared/assets/icons/redesign/share.svg';
import Edit from '../../../shared/assets/icons/redesign/pencil.svg';
import Bookmark from '../../../shared/assets/icons/redesign/bookmark.svg';
import ChevronLeft from '../../../shared/assets/icons/redesign/chevron-left.svg';
import { canEditContent } from '../../../helpers/user';

export default function Article({
  id,
  breadCrumbs,
  title,
  imageUrl,
  contentHTML,
  user,
  ads,
  expert,
  metadata,
  author,
  locale,
  tags,
  config,
  layout,
  bookmarks,
  removeBookmark,
  addBookmark,
  contentUri,
  loginUrl,
  signupUrl,
  verifier,
  formData,
  authorUser,
  addFormAnswer,
  isCategory,
  intro,
  submitFormAnswer,
  isPreviewMode,
  commentsEnabled,
  isTag,
  uri,
  comments,
  addComment,
  editComment,
  deleteComment,
  query,
  changeUserData,
  addUserEvent,
  ...props
}) {
  const [isMobile] = useMobile();
  const ref = useRef();
  const router = useRouter();
  const [loginPopup, showLoginPopup] = useState(false);

  const contentImg = useRef();
  const channelImg = useRef();
  const contentImgDimensions = useDimensions(contentImg);
  const channelImgDimensions = useDimensions(channelImg);

  const bookmark = bookmarks?.find(
    ({ content }) => content?.uri.toLowerCase() === contentUri.toLowerCase()
  );

  const [adName, adUrl] = getAdvertisementData(metadata) || [];
  const contentReference = getContentReference(metadata);

  const updatedAt = convert(props.updatedAt, locale);
  const postedAt = convert(props.postedAt.replace(/-/g, '/'), locale);

  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (window?.iFrameResize) window.iFrameResize({ log: false }, '#formframe');
  }, []);

  useEffect(() => {
    if (layout === 'layout-theme') {
      ['ebookList', 'groupList', 'courseList'].forEach((className) => {
        const el = document.querySelector(`.${className}`);

        if (el?.previousSibling?.classList?.contains('article-content-item')) {
          el.previousSibling
            .querySelector('.layout-columns:last-of-type')
            .appendChild(el);

          el.style.display = 'block';
        }
      });

      document
        .querySelectorAll('.article-content-item')
        .forEach((item, idx) => {
          item.classList.add(`layout-columns-section-${idx + 1}`);
        });
    }
  }, []);

  const isCalendarPage = ['zwangerschapskalender', 'groeikalender'].includes(
    uri
  );

  return (
    <>
      <div className="article">
        {!isCategory && (
          <a
            className="article-back-btn cursor-pointer"
            onClick={() => router.back()}
          >
            <ChevronLeft className="article-back-btn-arrow" />
            Terug
          </a>
        )}

        <Breadcrumbs items={breadCrumbs} />

        <article>
          <p className="article-title">{title}</p>

          <div className="article-publish-data article-publish-data-mobile">
            {isCategory ? (
              <>
                <span>
                  <strong>Geschreven door: </strong>
                  <a
                    href={authorUser ? authorUser.website : author.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {authorUser ? authorUser.displayName : author.displayName}
                  </a>
                </span>

                {verifier.displayName && (
                  <span>
                    <Verifier {...verifier} />
                  </span>
                )}
              </>
            ) : (
              <>
                {' '}
                {postedAt && (
                  <span>
                    <strong>Gepubliceerd op: </strong>
                    {postedAt}
                  </span>
                )}
                {postedAt && updatedAt && (
                  <span>
                    <strong>Bewerkt op: </strong>
                    {updatedAt}
                  </span>
                )}
              </>
            )}
          </div>

          <div className="article-action-bar">
            <div className="article-channel">
              <div className="article-channel-img" ref={channelImg}>
                {channelImgDimensions && (
                  <img
                    alt="author-image"
                    srcSet={srcSet(author.imageUrl, channelImgDimensions)}
                  />
                )}
              </div>

              <div>
                <Link href={author.link} prefetch={false}>
                  <span className="article-channel-name cursor-pointer">
                    {author.displayName}
                  </span>
                </Link>
                <span className="article-publish-data _desktop">
                  {isCategory ? (
                    <>
                      <span>
                        <strong>Geschreven door: </strong>
                        <a
                          href={authorUser ? authorUser.website : author.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {authorUser
                            ? authorUser.displayName
                            : author.displayName}
                        </a>
                      </span>

                      {verifier.displayName && (
                        <span>
                          <Dot className="article-dot-icon" />
                          <Verifier {...verifier} />
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {' '}
                      {postedAt && (
                        <span>
                          <strong>Gepubliceerd op: </strong>
                          {postedAt}
                        </span>
                      )}
                      {postedAt && updatedAt && (
                        <span>
                          <Dot className="article-dot-icon" />
                          <strong>Bewerkt op: </strong>
                          {updatedAt}
                        </span>
                      )}
                    </>
                  )}
                </span>
              </div>
            </div>
            <div>
              {canEditContent(user) && (
                <a
                  href={`https://www1.zwangerenportaal.nl/cms/contents/${id}/edit`}
                  target="_blank"
                >
                  <Edit className="article-edit-icon cursor-pointer" />
                </a>
              )}
              <Share
                className="article-share-icon"
                onClick={() =>
                  navigator.share &&
                  navigator.share({
                    url: router.asPath
                  })
                }
              />
              <Bookmark
                onClick={bookmarkContent}
                className={getString([
                  {
                    value: 'article-bookmark-icon',
                    condition: true
                  },
                  {
                    value: 'article-bookmark-icon-active',
                    condition: bookmark
                  }
                ])}
              />
            </div>
          </div>

          {!isCategory && (
            <>
              <div className="article-img-wrap" ref={contentImg}>
                {isMobile ? (
                  <img
                    src={imageUrl
                      .replace('/w/', `/1700/`)
                      .replace('/h/', `/450/`)}
                    alt="article-image"
                  />
                ) : (
                  contentImgDimensions && (
                    <img
                      alt="article-image"
                      srcSet={srcSet(imageUrl, contentImgDimensions)}
                    />
                  )
                )}
              </div>

              <div className="article-contributors">
                <span className="article-contributor">
                  {authorUser ? (
                    <>
                      <strong>Auteur: </strong>
                      <a
                        href={authorUser.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {authorUser.displayName}
                      </a>
                    </>
                  ) : (
                    <>
                      <strong>Geschreven door: </strong>
                      <a
                        href={author.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {author.displayName}
                      </a>
                    </>
                  )}
                </span>

                {verifier.displayName && (
                  <span className="article-contributor">
                    <Verifier {...verifier} />
                  </span>
                )}

                {adName && (
                  <span className="article-contributor">
                    <strong>In samenwerking met: </strong>
                    <a href={adUrl} target="_blank" rel="noopener noreferrer">
                      {adName}
                    </a>
                  </span>
                )}
              </div>
            </>
          )}

          <div className="article-content">
            {uri === 'uitgerekende-datum' && (
              <CalculationTool
                type="dueDate"
                initialDate={query.date}
                username={user.username}
                loginUrl={config.LOGIN_URL}
                changeUserData={changeUserData}
              />
            )}

            {uri === 'Vruchtbare-dagen-berekenen' && (
              <CalculationTool type="fertility" loginUrl={config.LOGIN_URL} />
            )}

            <div className="article-intro bold">{intro}</div>

            {isCalendarPage && (
              <ContentCalendarCarousel
                uri={uri}
                user={user}
                showLoginPopup={showLoginPopup}
              />
            )}

            {parseContentHTML(contentHTML).map(
              ({ html, component: Component, args }, idx) => {
                if (html)
                  return (
                    <div
                      className="article-content-item"
                      key={idx}
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  );

                if (Component)
                  return (
                    <Component
                      key={idx}
                      {...props}
                      {...args}
                      user={user}
                      config={config}
                      username={user.username}
                      addUserEvent={addUserEvent}
                      addFormAnswer={addFormAnswer}
                      submitFormAnswer={submitFormAnswer}
                    />
                  );
              }
            )}
          </div>

          {ads && <ArticleAdvertisement ads={ads} />}

          {expert.displayName && <ExpertBanner expert={expert} />}

          {contentHTML && formData.form && (
            <div className="article-survey">
              <LazyLoadComponent>
                <SurveyQuickForm
                  item={formData.form}
                  addUserEvent={addUserEvent}
                  addFormAnswer={addFormAnswer}
                  metadata={{ contentTitle: title }}
                  submitFormAnswer={submitFormAnswer}
                  completeButtonLabel="Verzenden"
                />
              </LazyLoadComponent>
            </div>
          )}
        </article>

        {!!tags.length && (
          <div className="article-tags">
            <p className="article-tags-title">Tags</p>

            <ul className="article-tag-list">
              {tags.map((tag) => (
                <Link key={tag.id} href={`/${tag.uri}`} prefetch={false}>
                  <li className="article-tag cursor-pointer">{tag.title}</li>
                </Link>
              ))}
            </ul>
          </div>
        )}

        {contentReference && (
          <div className="article-reference">
            Bron:{' '}
            {contentReference.map((item, idx) => (
              <>
                <a
                  className="article-reference-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={item.link}
                >
                  {item.title}
                </a>
                {idx < contentReference.length - 1 && ', '}
              </>
            ))}
          </div>
        )}

        {!isPreviewMode && commentsEnabled && !isTag && !isCategory && (
          <LazyLoadComponent>
            <CommentList
              uri={uri}
              user={user}
              comments={comments}
              addComment={addComment}
              editComment={editComment}
              deleteComment={deleteComment}
              showLoginPopup={showLoginPopup}
            />
          </LazyLoadComponent>
        )}
      </div>

      <Transition on={loginPopup}>
        <div className="popup-wrapper">
          <Popup onClose={() => showLoginPopup(false)}>
            <LoginPopupContent loginUrl={loginUrl} signupUrl={signupUrl} />
          </Popup>
        </div>
      </Transition>
    </>
  );

  function Verifier({ link, displayName }) {
    return (
      <span>
        <strong>(Medisch) gereviewd door: </strong>
        <a href={link} target="_blank" rel="noopener noreferrer">
          {displayName}
        </a>
      </span>
    );
  }

  function bookmarkContent() {
    if (!user.username) return showLoginPopup(true);

    if (isUpdating) return;

    setIsUpdating(true);

    if (bookmark)
      removeBookmark({ variables: { id: bookmark.id } }).then(() =>
        setIsUpdating(false)
      );
    else
      addBookmark({ variables: { contentUri } }).then(() =>
        setIsUpdating(false)
      );
  }
}
